/** Empty invoice template used by Add Sanction Order form / draft mapping. */
export const emptyInvoice = () => ({
  vendor_id: null,
  vendor_account_id: null,
  payment: 1,
  invoice_no: null,
  invoice_ref_no: null,
  invoice_date: null,
  invoice_value: null,
  taxable_amount: null,
  sanction_amount: null,
  invoice: null,
  invoice_ref: null,
  invoice_url: null,
  invoice_ref_url: null,
  gst: null,
  gis: null,
  nps: null,
  tds_it_section: null,
  tds_it_rate: null,
  tds_it_other_rate: null,
  tds_it_amount: null,
  s_gst_rate: null,
  s_gst_amount: null,
  c_gst_rate: null,
  c_gst_amount: null,
  i_gst_rate: null,
  i_gst_amount: null,
  royalty_rate: null,
  royalty_amount: null,
  labour_rate: null,
  labour_amount: null,
  other_deduction_rate: null,
  other_deduction: null,
  deduction_remarks: null,
  penalty_amount: null,
  withheld_amount: null,
  remarks: null,
  schemes: [
    {
      scheme_id: null,
      amount: null,
      scheme_deduction: 0,
      financial_year: null,
      subheads: [
        {
          sub_heads_id: null,
          sub_head_amount: null,
          activity: [
            {
              activity_id: "",
              subactivity_id: "",
              provisional_budget: 0,
              budget: 0,
              balance: "",
              activity_deduction: 0,
            },
          ],
        },
      ],
    },
  ],
});

export const emptyVoucher = () => ({
  voucher_no: null,
  voucher_date: null,
  total_voucher_value: null,
  voucher: null,
  supporting_docs: null,
  voucher_url: null,
  supporting_docs_url: null,
  invoices: [emptyInvoice()],
});

export const emptySanctionForm = () => ({
  financial_year: null,
  sanction_order_no: null,
  sanction_order_date: null,
  sanction_order_value: null,
  sanction_order: null,
  sanction_order_url: null,
  vouchers: [emptyVoucher()],
});

const isFilled = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  if (value instanceof FileList) return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const isFileUpload = (value) =>
  value instanceof FileList ||
  (Array.isArray(value) && value[0] instanceof File) ||
  value instanceof File;

/** True when value looks like an existing uploaded document URL. */
export const hasExistingDocUrl = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return false;
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith("/");
};

/** True when a new File is selected in a file input / FileList. */
export const hasNewFileUpload = (value) => {
  if (value instanceof File) return true;
  if (value instanceof FileList) return value.length > 0 && value[0] instanceof File;
  if (Array.isArray(value)) return value[0] instanceof File;
  return false;
};

/**
 * Any-one-of-three rule: new upload OR existing URL on sanction / voucher / invoice.
 * Used on Submit for Approval (preview) for both create and continue-draft.
 */
export const hasAnyOrderDocument = (formData) => {
  if (!formData) return false;
  if (
    hasNewFileUpload(formData.sanction_order) ||
    hasExistingDocUrl(formData.sanction_order_url) ||
    hasExistingDocUrl(formData.sanction_order)
  ) {
    return true;
  }
  return (formData.vouchers || []).some((voucher) => {
    if (
      hasNewFileUpload(voucher.voucher) ||
      hasExistingDocUrl(voucher.voucher_url) ||
      hasExistingDocUrl(voucher.voucher)
    ) {
      return true;
    }
    return (voucher.invoices || []).some(
      (invoice) =>
        hasNewFileUpload(invoice.invoice) ||
        hasExistingDocUrl(invoice.invoice_url) ||
        hasExistingDocUrl(invoice.invoice)
    );
  });
};

/** True if user entered anything worth saving as draft. */
export const hasDraftContent = (formData) => {
  if (!formData) return false;
  if (
    isFilled(formData.sanction_order_no) ||
    isFilled(formData.sanction_order_date) ||
    isFilled(formData.sanction_order_value) ||
    isFileUpload(formData.sanction_order) ||
    isFilled(formData.sanction_order_url)
  ) {
    return true;
  }

  for (const voucher of formData.vouchers || []) {
    if (
      isFilled(voucher.voucher_no) ||
      isFilled(voucher.voucher_date) ||
      isFilled(voucher.total_voucher_value) ||
      isFileUpload(voucher.voucher) ||
      isFileUpload(voucher.supporting_docs) ||
      isFilled(voucher.voucher_url)
    ) {
      return true;
    }
    for (const invoice of voucher.invoices || []) {
      if (
        isFilled(invoice.vendor_id) ||
        isFilled(invoice.invoice_no) ||
        isFilled(invoice.invoice_date) ||
        isFilled(invoice.invoice_value) ||
        isFilled(invoice.taxable_amount) ||
        isFilled(invoice.sanction_amount) ||
        isFileUpload(invoice.invoice) ||
        isFileUpload(invoice.invoice_ref) ||
        isFilled(invoice.invoice_url) ||
        isFilled(invoice.remarks)
      ) {
        return true;
      }
      for (const scheme of invoice.schemes || []) {
        if (isFilled(scheme.scheme_id) || isFilled(scheme.amount)) return true;
        for (const subhead of scheme.subheads || []) {
          if (isFilled(subhead.sub_heads_id) || isFilled(subhead.sub_head_amount)) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

/** Build multipart FormData matching live sanction_order create payload. */
export const buildSanctionOrderFormData = (
  formData,
  { addedBy, draftId, forLiveSubmit = false } = {}
) => {
  const formDataObject = new FormData();
  if (draftId) {
    formDataObject.append("draft_id", draftId);
  }
  if (formData.financial_year != null && formData.financial_year !== "") {
    formDataObject.append("financial_year", formData.financial_year);
  }
  formDataObject.append("sanction_order_no", formData.sanction_order_no ?? "");
  formDataObject.append("sanction_order_date", formData.sanction_order_date ?? "");
  formDataObject.append("sanction_order_value", formData.sanction_order_value ?? "");
  if (addedBy != null) {
    formDataObject.append("added_by", addedBy);
  }

  if (formData.sanction_order?.[0]) {
    formDataObject.append("sanction_order", formData.sanction_order[0]);
  } else if (hasExistingDocUrl(formData.sanction_order_url)) {
    // Continue-draft / re-submit: tell BE existing draft URL still applies
    formDataObject.append("existing_sanction_order", formData.sanction_order_url);
  }

  (formData.vouchers || []).forEach((voucher, index) => {
    formDataObject.append(`vouchers[${index}][voucher_no]`, voucher.voucher_no ?? "");
    formDataObject.append(`vouchers[${index}][voucher_date]`, voucher.voucher_date ?? "");
    formDataObject.append(
      `vouchers[${index}][total_voucher_value]`,
      voucher.total_voucher_value ?? ""
    );
    // Draft-only PK — never send on live submit (would collide with live vouchers.id)
    if (!forLiveSubmit && voucher.id != null && voucher.id !== "") {
      formDataObject.append(`vouchers[${index}][id]`, voucher.id);
    }
    if (voucher.voucher?.[0]) {
      formDataObject.append(`vouchers[${index}][voucher]`, voucher.voucher[0]);
    } else if (hasExistingDocUrl(voucher.voucher_url)) {
      formDataObject.append(
        `vouchers[${index}][existing_voucher]`,
        voucher.voucher_url
      );
    }
    if (voucher.supporting_docs?.[0]) {
      formDataObject.append(
        `vouchers[${index}][supporting_docs]`,
        voucher.supporting_docs[0]
      );
    }
    (voucher.invoices || []).forEach((invoice, j) => {
      if (invoice.invoice?.[0]) {
        formDataObject.append(`invoices[${index}][${j}][invoice]`, invoice.invoice[0]);
      } else if (hasExistingDocUrl(invoice.invoice_url)) {
        formDataObject.append(
          `invoices[${index}][${j}][existing_invoice]`,
          invoice.invoice_url
        );
      }
      if (invoice.invoice_ref?.[0]) {
        formDataObject.append(
          `invoices[${index}][${j}][invoice_ref]`,
          invoice.invoice_ref[0]
        );
      }
      // Strip FileList / UI-only URL helpers before JSON stringify
      const {
        invoice: _invFile,
        invoice_ref: _refFile,
        invoice_url: _invUrl,
        invoice_ref_url: _refUrl,
        id: _draftInvoiceId,
        ...invoiceJson
      } = invoice;
      // Keep existing invoice URL inside JSON so BE can count it for any-one-of-three
      if (!_invFile?.[0] && hasExistingDocUrl(_invUrl)) {
        invoiceJson.existing_invoice = _invUrl;
      }
      // Live submit must not carry draft_invoices.id (or any draft child PK).
      // Draft save/update may keep id so BE can update the same draft rows.
      if (!forLiveSubmit && _draftInvoiceId != null && _draftInvoiceId !== "") {
        invoiceJson.id = _draftInvoiceId;
      } else {
        delete invoiceJson.id;
      }
      formDataObject.append(
        `vouchers[${index}][invoices][${j}]`,
        JSON.stringify(invoiceJson)
      );
    });
  });

  return formDataObject;
};

const mapActivity = (activity = {}) => ({
  activity_id:
    activity.activity_id != null && activity.activity_id !== ""
      ? String(activity.activity_id)
      : "",
  subactivity_id:
    activity.subactivity_id != null && activity.subactivity_id !== ""
      ? String(activity.subactivity_id)
      : "",
  provisional_budget: activity.provisional_budget ?? 0,
  budget: activity.budget ?? 0,
  balance: activity.balance ?? activity.amount ?? "",
  activity_deduction: activity.activity_deduction ?? 0,
});

const mapSubhead = (subhead = {}) => ({
  sub_heads_id:
    subhead.sub_heads_id != null && subhead.sub_heads_id !== ""
      ? String(subhead.sub_heads_id)
      : null,
  sub_head_amount: subhead.sub_head_amount ?? null,
  subhead_budget_amount: subhead.subhead_budget_amount ?? null,
  subhead_deduction: subhead.subhead_deduction ?? 0,
  activity: (subhead.activity || [{}]).map(mapActivity),
});

const mapScheme = (scheme = {}) => ({
  scheme_id:
    scheme.scheme_id != null && scheme.scheme_id !== ""
      ? String(scheme.scheme_id)
      : null,
  amount: scheme.amount ?? null,
  scheme_deduction: scheme.scheme_deduction ?? 0,
  financial_year:
    scheme.financial_year != null && scheme.financial_year !== ""
      ? String(scheme.financial_year)
      : null,
  available_balance: scheme.available_balance ?? null,
  subheads: (scheme.subheads || [{}]).map(mapSubhead),
});

const mapInvoice = (invoice = {}) => {
  const base = emptyInvoice();
  const payment =
    invoice.payment ?? invoice.payment_type ?? base.payment;
  return {
    ...base,
    ...invoice,
    vendor_id:
      invoice.vendor_id != null && invoice.vendor_id !== ""
        ? String(invoice.vendor_id)
        : null,
    vendor_account_id:
      invoice.vendor_account_id != null && invoice.vendor_account_id !== ""
        ? String(invoice.vendor_account_id)
        : null,
    payment: payment === "2" || payment === 2 ? 2 : 1,
    invoice: null,
    invoice_ref: null,
    invoice_url:
      typeof invoice.invoice === "string" ? invoice.invoice : invoice.invoice_url || null,
    invoice_ref_url:
      typeof invoice.invoice_ref === "string"
        ? invoice.invoice_ref
        : invoice.invoice_ref_url || null,
    schemes: (invoice.schemes || [{}]).map(mapScheme),
  };
};

const mapVoucher = (voucher = {}) => ({
  ...emptyVoucher(),
  ...voucher,
  voucher: null,
  supporting_docs: null,
  voucher_url:
    typeof voucher.voucher === "string" ? voucher.voucher : voucher.voucher_url || null,
  supporting_docs_url:
    typeof voucher.supporting_docs === "string"
      ? voucher.supporting_docs
      : voucher.supporting_docs_url || null,
  invoices: (voucher.invoices || [{}]).map(mapInvoice),
});

/** Map GET draft detail → form state. */
export const mapDraftDetailsToFormData = (details) => {
  if (!details) return emptySanctionForm();
  return {
    financial_year: details.financial_year ?? null,
    sanction_order_no: details.sanction_order_no ?? null,
    sanction_order_date: details.sanction_order_date ?? null,
    sanction_order_value: details.sanction_order_value ?? null,
    sanction_order: null,
    sanction_order_url:
      typeof details.sanction_order === "string" && details.sanction_order.trim()
        ? details.sanction_order.trim()
        : typeof details.sanction_order_url === "string" &&
            details.sanction_order_url.trim()
          ? details.sanction_order_url.trim()
          : null,
    vouchers:
      details.vouchers?.length > 0
        ? details.vouchers.map(mapVoucher)
        : [emptyVoucher()],
  };
};
